// @core/stores/project/ProjectBoardStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  Board,
  CreateBoardRequest,
  UpdateBoardRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectBoardStore {
  // List state
  items: Board[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Board | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Board>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Board | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Board | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create board
   */
  async CreateBoard(project_id: string, data: CreateBoardRequest): Promise<Board> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateBoard(project_id, data);
      const item = response.board;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete board
   */
  async DeleteBoard(project_id: string, board_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteBoard(project_id, board_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== board_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === board_id) {
          this.currentItem = null;
        }

        this.removeFromCache(board_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get board
   */
  async GetBoard(project_id: string, board_id: string): Promise<Board> {
    // Check cache first
    const cached = this.cache.get(board_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetBoard(project_id, board_id);
      const item = response.board;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get board view
   */
  async GetBoardView(project_id: string, board_id: string): Promise<Board> {
    // Check cache first
    const cached = this.cache.get(board_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetBoardView(project_id, board_id);
      const item = response.board;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List boards
   */
  async ListBoards(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListBoards(project_id, params);

      runInAction(() => {
        this.items = response.boards?.boards ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update board
   */
  async UpdateBoard(project_id: string, board_id: string, data: UpdateBoardRequest): Promise<Board> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateBoard(project_id, board_id, data);
      const item = response.board;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectBoardStore = new ProjectBoardStore();
